import { useEffect, useMemo, useRef, useState } from "react";
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Paper,
  useTheme,
  TextField,
  Typography,
  Divider,
  Button,
  Tooltip,
  CircularProgress,
  LinearProgress,
  Select,
  MenuItem,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import DetailsCard from "../../Cashier/v1/components/CashierDetailsComponent/DetailsCard";
import CloseIcon from "@mui/icons-material/Close";
import DoneIcon from "@mui/icons-material/Done";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "react-query";
import {
  deleteBillItem,
  editBillDetails,
  getBillingDetails,
  printOutHouse,
  saveBill,
} from "../services";
import CustomTableBody from "../../../utilis/component/CustomTableBody";
import { BillingDetailsListType } from "../types";
import usePopup from "../../../hooks/usePopup";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import usePrint from "@hooks/usePrint";
import PrintIcon from "@mui/icons-material/Print";
import useAuthentication from "@hooks/useAuthentication";
import { PatientFullDetailsType } from "../../PatientBooking/v1/types";
import PercentIcon from "@mui/icons-material/Percent";

const roundMoney = (n: number) => Math.round(n * 100) / 100;

type BillingDiscountKind = "Percentage" | "Amount";

function normalizeDiscountType(
  t: BillingDetailsListType["discountType"] | string | undefined,
): BillingDiscountKind {
  if (t === "Percentage" || t === "Amount") return t;
  if (t === "percent") return "Percentage";
  if (t === "amount") return "Amount";
  return "Percentage";
}

/** API sends null — only explicit true means excluded */
function isLineExcluded(line: { isExcluded?: boolean | null }): boolean {
  return line.isExcluded === true;
}

function getDiscountInput(line: BillingDetailsListType): number {
  const dt = normalizeDiscountType(line.discountType);
  if (dt === "Percentage") {
    return Number(line.discountPercentage) || 0;
  }
  return Number(line.discountAmount) || 0;
}

/** Line gross for display — AMOUNT column always shows rate × qty (not API totalAmount / net). */
function getLineGrossDisplay(line: {
  rate?: number;
  quantity?: number;
}): number {
  const r = Number(line.rate) || 0;
  const q = Math.max(0, Number(line.quantity) || 0);
  return roundMoney(r * q);
}

/** Patient column: insured uses patShare; excluded/cash uses patient payable (patShare when set, else totalAmount). */
function getPatientShareDisplay(line: BillingDetailsListType): number {
  if (isLineExcluded(line)) {
    const p = line.patShare;
    if (p != null && !Number.isNaN(Number(p))) {
      return roundMoney(Number(p));
    }
    return roundMoney(Number(line.totalAmount) || 0);
  }
  return roundMoney(Number(line.patShare) || 0);
}

/** Patient-side discount on the line (amount shaved off patient responsibility before patShare). */
function getLinePatientDiscountAmount(line: BillingDetailsListType): number {
  const gross = getLineGrossDisplay(line);
  const pat = getPatientShareDisplay(line);
  if (isLineExcluded(line)) {
    return roundMoney(Math.max(0, gross - pat));
  }
  const insD = Number(line.insDiscount) || 0;
  const insA = Number(line.insAmount) || 0;
  const patientBase = roundMoney(Math.max(0, gross - insD - insA));
  return roundMoney(Math.max(0, patientBase - pat));
}

type EditLineSnapshot = {
  /** From API; may not equal rate×qty — do not use alone as scaling denominator */
  totalAmount: number;
  quantity: number;
  insDiscount: number;
  insAmount: number;
  rate: number;
  /** Row patient share from API — used when recomputation collapses to 0 incorrectly */
  patShare: number;
  /** Discount state at edit-open; used to avoid recompute when nothing changed */
  discountType: BillingDiscountKind;
  discountValue: number;
};

type EditableLine = BillingDetailsListType & { encounterId: number };

function computeBillingLineAmounts(
  rate: number,
  quantity: number,
  isExcluded: boolean,
  snapshot: EditLineSnapshot,
  discountType: BillingDiscountKind,
  discountValue: number,
) {
  const qty = Math.max(0, Number(quantity) || 0);
  const r = Number(rate) || 0;
  let gross = roundMoney(r * qty);

  let insDiscount = Number(snapshot.insDiscount) || 0;
  let insAmount = Number(snapshot.insAmount) || 0;

  let baselineGross = roundMoney(
    (Number(snapshot.rate) || 0) * Math.max(0, Number(snapshot.quantity) || 0),
  );
  const snapshotTotal = roundMoney(Number(snapshot.totalAmount) || 0);
  /** Insurance rows: backend sometimes omits rate/qty but sends totalAmount + splits — avoid scaling everything to 0 */
  if (!isExcluded && snapshotTotal > 0) {
    if (gross <= 0) gross = snapshotTotal;
    if (baselineGross <= 0) baselineGross = snapshotTotal;
  }

  if (!isExcluded) {
    // Keep insurance contribution stable on edit (from API snapshot).
    // User edits should affect patient share (and patient discount), not re-scale insurer share.
    insDiscount = roundMoney(Number(snapshot.insDiscount) || 0);
    insAmount = roundMoney(Number(snapshot.insAmount) || 0);
  } else {
    insDiscount = 0;
    insAmount = 0;
  }

  // Guardrail: insurance components can never exceed gross; otherwise patientBase goes negative
  // and UI collapses patShare to 0 when editing qty/discount.
  insDiscount = roundMoney(Math.max(0, Math.min(insDiscount, gross)));
  insAmount = roundMoney(Math.max(0, insAmount));
  const insSum = roundMoney(insDiscount + insAmount);
  if (insSum > gross && insSum > 0) {
    const factor = gross / insSum;
    insDiscount = roundMoney(insDiscount * factor);
    insAmount = roundMoney(gross - insDiscount);
  } else {
    insAmount = roundMoney(Math.min(insAmount, Math.max(0, gross - insDiscount)));
  }

  if (isExcluded) {
    const dv = Number(discountValue) || 0;
    let patientDiscountAmount =
      discountType === "Percentage"
        ? roundMoney((gross * Math.min(100, Math.max(0, dv))) / 100)
        : roundMoney(Math.max(0, dv));
    patientDiscountAmount = Math.min(patientDiscountAmount, gross);
    const netPatient = roundMoney(Math.max(0, gross - patientDiscountAmount));
    return {
      totalAmount: netPatient,
      insDiscount: 0,
      insAmount: 0,
      patShare: netPatient,
    };
  }

  // Insurance: treat API patShare as the source-of-truth.
  // Compute a pre-discount base from snapshot (so we don't double-apply discount),
  // then scale by qty and apply the *current* discount.
  const patientBaseMax = roundMoney(Math.max(0, gross - insDiscount - insAmount));
  const snapshotPs = roundMoney(Number(snapshot.patShare) || 0);
  const snapDv = Number(snapshot.discountValue) || 0;
  const snapshotPatShareBeforeDiscount = (() => {
    if (snapshot.discountType === "Percentage") {
      const pct = Math.min(100, Math.max(0, snapDv));
      const denom = 1 - pct / 100;
      if (denom <= 0) return 0;
      return roundMoney(snapshotPs / denom);
    }
    // Amount discount: patShare = base - discountAmount
    return roundMoney(snapshotPs + Math.max(0, snapDv));
  })();

  // Use quantity ratio (count change). Using gross/rate can introduce errors when backend totals/rates differ.
  const ratio = snapshot.quantity > 0 ? qty / snapshot.quantity : 1;
  const scaledBaseBeforeDiscount = roundMoney(
    Math.max(0, snapshotPatShareBeforeDiscount * ratio),
  );

  const dv = Number(discountValue) || 0;
  let patientDiscountAmount =
    discountType === "Percentage"
      ? roundMoney(
          (scaledBaseBeforeDiscount * Math.min(100, Math.max(0, dv))) / 100,
        )
      : roundMoney(Math.max(0, dv));
  patientDiscountAmount = Math.min(
    patientDiscountAmount,
    scaledBaseBeforeDiscount,
  );

  let patShare = roundMoney(
    Math.max(0, scaledBaseBeforeDiscount - patientDiscountAmount),
  );
  // Don't allow patient share to exceed the maximum patient-responsible portion.
  patShare = Math.min(patShare, patientBaseMax);

  return {
    totalAmount: gross,
    insDiscount,
    insAmount,
    patShare,
  };
}

function patchEditableLine(
  prev: EditableLine | null,
  patch: Partial<EditableLine>,
  snapshot: EditLineSnapshot | null,
): EditableLine | null {
  if (!prev || !snapshot) return prev;
  const next: EditableLine = { ...prev, ...patch };
  const discType = normalizeDiscountType(next.discountType);
  const discVal = getDiscountInput(next);
  const computed = computeBillingLineAmounts(
    next.rate,
    next.quantity,
    isLineExcluded(next),
    snapshot,
    discType,
    discVal,
  );
  return {
    ...next,
    ...computed,
    discountType: discType,
    discountPercentage: discType === "Percentage" ? discVal : null,
    discountAmount: discType === "Amount" ? discVal : null,
  };
}

function BillingDetailsPage() {
  const theme = useTheme();
  const [details, setDetails] = useState<PatientFullDetailsType | null>(null);
  const { showSnackbar } = usePopup();
  const { encounterId } = useParams();
  const { printPdf, loading } = usePrint();
  const { user } = useAuthentication();
  const navigateTo = useNavigate();
  const [edit, setEdit] = useState<EditableLine | null>(null);
  const [isSaved, setIsSaved] = useState<boolean>(false);
  const [selected, setSelected] = useState<number>(-1);
  const editSnapshotRef = useRef<EditLineSnapshot | null>(null);
  const memoizedEncounterId = useMemo(() => encounterId, [encounterId]);

  const {
    data: allBills,
    refetch,
    isFetching,
    isLoading,
  } = useQuery(
    ["fetchBillingDetails", memoizedEncounterId],
    () =>
      getBillingDetails(
        encounterId as string,
        details?.insurancePatient?.insurance_name,
        String(user?.codetype),
      ),
    {
      enabled: !!memoizedEncounterId && !!details,
      retry: 3,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  );

  const amountSummary = useMemo(() => {
    const rows = allBills?.response?.billingDetails;
    if (!rows?.length) return null;
    return rows.reduce(
      (acc, row, index) => {
        const line =
          edit?.id === row.id && selected === index && edit ? edit : row;
        const pat = getPatientShareDisplay(line);
        const gross = getLineGrossDisplay(line);
        const patDisc = getLinePatientDiscountAmount(line);
        return {
          totalAmount: acc.totalAmount + gross,
          insDiscount: acc.insDiscount + line.insDiscount,
          insAmount: acc.insAmount + line.insAmount,
          patShare: acc.patShare + pat,
          totalDiscountedAmount: acc.totalDiscountedAmount + patDisc,
        };
      },
      {
        totalAmount: 0,
        insDiscount: 0,
        insAmount: 0,
        patShare: 0,
        totalDiscountedAmount: 0,
      },
    );
  }, [allBills?.response?.billingDetails, edit, selected]);

  const { mutate: save, isLoading: saveLoading } = useMutation(saveBill, {
    onSuccess: (result) => {
      if (result.responseIndicator == "success") {
        showSnackbar({
          message: result.responseMessage,
          open: true,
          variant: "success",
          onClose: goBack,
          duration: 3000,
        });
        setIsSaved(true);
      } else {
        showSnackbar({
          message: result.responseMessage,
          open: true,
          variant: "warning",
          duration: 3000,
        });
      }
    },
    onError: (e: any) => {
      showSnackbar({
        message: e?.response?.data?.responseMessage || "Something went wrong !",
        open: true,
        variant: "warning",
        duration: 3000,
      });
    },
  });
  const { mutate: saveEdit, isLoading: saveEditLoading } = useMutation(
    editBillDetails,
    {
      onSuccess: (result) => {
        if (result?.responseIndicator == "success") {
          showSnackbar({
            message: result.responseMessage,
            open: true,
            variant: "success",
            duration: 3000,
          });
          setEdit(null);
          refetch();
        } else {
          showSnackbar({
            message: result.responseMessage,
            open: true,
            variant: "warning",
            duration: 3000,
          });
        }
      },
      onError: (e: any) => {
        showSnackbar({
          message: e.response.data.responseMessage || "Something went wrong !",
          open: true,
          variant: "warning",
          duration: 3000,
        });
      },
    },
  );

  const { mutate: deleteBill, isLoading: deleteLoading } = useMutation(
    deleteBillItem,
    {
      onSuccess: (result) => {
        if (result.responseIndicator == "success") {
          showSnackbar({
            message: result.responseMessage,
            open: true,
            variant: "success",
            duration: 3000,
          });
          setSelected(-1);
          setEdit(null);
          refetch();
        } else {
          showSnackbar({
            message: result.responseMessage,
            open: true,
            variant: "warning",
            duration: 3000,
          });
        }
      },
      onError: (e: any) => {
        showSnackbar({
          message: e.response.data.responseMessage || "Something went wrong !",
          open: true,
          variant: "warning",
          duration: 3000,
        });
      },
    },
  );

  const { mutate: printOutHouseItems, isLoading: outHousePrintLoading } =
    useMutation(printOutHouse, {
      onSuccess: (result) => {
        if (result.responseIndicator == "success") {
          printPdf(result.response, "new-tab");
        } else {
          showSnackbar({
            message: result.responseMessage,
            open: true,
            variant: "info",
            duration: 3000,
          });
        }
      },
      onError: (e: any) => {
        showSnackbar({
          message: e.response.data.responseMessage || "Something went wrong !",
          open: true,
          variant: "warning",
          duration: 3000,
        });
      },
    });

  const handleEditClick = (detailsRow: EditableLine, index: number) => {
    const discountType = normalizeDiscountType(detailsRow.discountType);
    const discountValue = getDiscountInput({
      ...detailsRow,
      discountType,
    });
    editSnapshotRef.current = {
      totalAmount: Number(detailsRow.totalAmount) || 0,
      quantity: Math.max(0, Number(detailsRow.quantity) || 0),
      insDiscount: Number(detailsRow.insDiscount) || 0,
      insAmount: Number(detailsRow.insAmount) || 0,
      rate: Number(detailsRow.rate) || 0,
      patShare: Number(detailsRow.patShare) || 0,
      discountType,
      discountValue,
    };
    const base: EditableLine = {
      ...detailsRow,
      discountType,
    };
    // Important: do NOT recompute amounts on edit-open; some CPT lines collapse patShare to 0.
    // Recompute only when user actually edits qty/discount (onChange) or at save time if changed.
    setEdit(base);
    setSelected(index);
  };

  /** Sends the full row (all keys from API + encounterId + discount) — same shape as before, with discount + recomputed amounts */
  const handleEditSave = () => {
    if (!edit) return;
    const snap = editSnapshotRef.current;
    const payload = (() => {
      if (!snap) return edit;
      const currentDiscType = normalizeDiscountType(edit.discountType);
      const currentDiscVal = getDiscountInput({
        ...edit,
        discountType: currentDiscType,
      });
      const qtySame = Math.max(0, Number(edit.quantity) || 0) === snap.quantity;
      const discSame =
        currentDiscType === snap.discountType &&
        Math.abs((Number(currentDiscVal) || 0) - (Number(snap.discountValue) || 0)) <
          0.005;
      // If user didn't change editable inputs, preserve API amounts (avoid patShare getting wiped).
      if (qtySame && discSame) {
        return {
          ...edit,
          discountType: currentDiscType,
          discountPercentage:
            currentDiscType === "Percentage" ? currentDiscVal : null,
          discountAmount: currentDiscType === "Amount" ? currentDiscVal : null,
        };
      }
      return patchEditableLine(
        { ...edit, discountType: currentDiscType },
        {},
        snap,
      ) ?? edit;
    })();
    saveEdit(payload);
  };

  const handleDelete = (detailsRow: EditableLine, index: number) => {
    deleteBill(detailsRow);
    setSelected(index);
  };

  const goBack = () => {
    navigateTo(-1);
  };

  const handleSave = () => {
    if (allBills?.response?.amountDetails) {
      let itemsForBill: BillingDetailsListType[] = [];
      if (allBills?.response?.billingDetails?.length) {
        allBills?.response?.billingDetails?.forEach((item, idx) => {
          const line =
            edit?.id === item.id && selected === idx && edit ? edit : item;
          itemsForBill.push({
            ...line,
            patShare: isLineExcluded(line) ? line.totalAmount : line.patShare,
            insAmount: isLineExcluded(line) ? 0 : line.insAmount,
          });
        });
      }
      const ad = allBills.response.amountDetails;
      const billSavePayload = {
        encounterId: parseInt(encounterId || "-1"),
        ...(amountSummary
          ? {
              totalAmount: amountSummary.totalAmount,
              insDiscount: amountSummary.insDiscount,
              insAmount: amountSummary.insAmount,
              patShare: amountSummary.patShare,
              totalPatShare: roundMoney(amountSummary.patShare),
              actualTotalAmount:displayAmountDetails?.totalAmount,
              totalDiscountedAmount: roundMoney(
                amountSummary.totalDiscountedAmount,
              ),
            }
          : {
              ...ad,
              totalPatShare: roundMoney(ad.patShare),
              actualTotalAmount: roundMoney(ad.patShare + ad.insAmount),
              totalDiscountedAmount: 0,
            }),
        itemsForBill,
        destination: "Billed",
      };
      save(billSavePayload);
      // console.log("allBills?.response?.billingDetails => ", billSavePayload);
    }
  };

  const handleOutHosePrint = () => {
    printOutHouseItems({ id: `${encounterId}` });
  };

  useEffect(() => {
    console.log("[details] => ", details);
  }, [details]);

  useEffect(() => {
    if (!edit) editSnapshotRef.current = null;
  }, [edit]);

  const displayAmountDetails =
    amountSummary ?? allBills?.response?.amountDetails;

  return (
    <div className="pt-4 px-2 pb-3 flex flex-col gap-y-3">
      <DetailsCard setDetails={setDetails} />
      <TableContainer component={Paper} className="shadow-lg rounded-lg border">
        {(isFetching || isLoading) && <LinearProgress />}

        <div className="p-2 flex justify-between items-center">
          <Typography fontWeight={600}>Bill details</Typography>
          <Button
            variant="outlined"
            endIcon={
              loading || outHousePrintLoading ? (
                <CircularProgress size={18} />
              ) : (
                <PrintIcon />
              )
            }
            disabled={loading || outHousePrintLoading}
            onClick={handleOutHosePrint}
          >
            print outhouse
          </Button>
        </div>
        <div className="max-h-[40dvh] overflow-y-auto">
          <Table size={"small"} stickyHeader>
            <TableHead>
              <TableRow className="">
                <TableCell
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.background.paper,
                  }}
                  className="font-semibold"
                >
                  SL.NO
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.background.paper,
                  }}
                  className="font-semibold"
                >
                  ITEM CODE
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.background.paper,
                  }}
                  className="font-semibold"
                >
                  NAME
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.background.paper,
                  }}
                  className="font-semibold"
                >
                  RATE
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.background.paper,
                  }}
                  className="font-semibold"
                >
                  UNIT
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.background.paper,
                  }}
                  className="font-semibold"
                >
                  AMOUNT
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.background.paper,
                  }}
                  className="font-semibold"
                >
                  DISCOUNT
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.background.paper,
                  }}
                  className="font-semibold"
                >
                  INS DISCOUNT
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.background.paper,
                  }}
                  className="font-semibold"
                >
                  INS AMOUNT
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.background.paper,
                  }}
                  className="font-semibold"
                >
                  PAT SHARE
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.background.paper,
                  }}
                  className="font-semibold"
                >
                  INS COVERED
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.background.paper,
                  }}
                  className="font-semibold"
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <CustomTableBody
              alert={"There is no items for billing"}
              dataLoading={false}
              isData={Boolean(allBills?.response?.billingDetails?.length)}
              totalSpan={{ col: 12, row: 5 }}
            >
              {allBills?.response?.billingDetails?.map((row, index) => {
                const line =
                  edit?.id === row.id && selected === index && edit
                    ? edit
                    : row;
                const lineDiscount = line as EditableLine;
                const isRowEditing = Boolean(
                  edit?.id == row?.id && selected == index,
                );
                const discTypeView = normalizeDiscountType(
                  lineDiscount.discountType,
                );
                const discValueView =
                  discTypeView === "Percentage"
                    ? lineDiscount.discountPercentage
                    : lineDiscount.discountAmount;
                const hasRowDiscount =
                  discValueView != null && discValueView > 0;
                return (
                  <TableRow key={row.id} className="">
                    <TableCell>{row.no ?? index + 1}</TableCell>
                    <TableCell>{row.itemCode}</TableCell>
                    <TableCell>{row.itemName}</TableCell>
                    <TableCell>{row.rate}</TableCell>
                    <TableCell>
                      {isRowEditing ? (
                        <TextField
                        disabled={String(row.cptGroupName).toLowerCase() == "dental"}
                          type="number"
                          variant="standard"
                          sx={{ width: 80 }}
                          size="small"
                          value={edit?.quantity}
                          inputProps={{
                            min: 0,
                          }}
                          onChange={(e) => {
                            const qty = parseInt(e.target.value || "0", 10);
                            setEdit(
                              (prev) =>
                                patchEditableLine(
                                  prev,
                                  { quantity: qty },
                                  editSnapshotRef.current,
                                ) ?? prev,
                            );
                          }}
                        />
                      ) : (
                        row?.quantity
                      )}
                    </TableCell>
                    <TableCell>{getLineGrossDisplay(line)}</TableCell>
                    <TableCell sx={{ minWidth: 220 }}>
                      {isRowEditing && edit ? (
                        <div className="flex flex-col">
                          <div className="flex items-center">
                            <TextField
                              label="Discount"
                              type="number"
                              size="small"
                              variant="outlined"
                              value={getDiscountInput(edit)}
                              inputProps={{ min: 0, step: 0.01 }}
                              onKeyDown={(e) => {
                                if (["e", "E", "+", "-"].includes(e.key)) {
                                  e.preventDefault();
                                }
                              }}
                              onChange={(e) => {
                                let v = parseFloat(e.target.value || "0");
                                const dt = normalizeDiscountType(
                                  edit.discountType,
                                );
                                if (!Number.isFinite(v)) v = 0;
                                if (v < 0) v = 0;
                                if (dt === "Percentage" && v > 100) v = 100;
                                setEdit(
                                  (prev) =>
                                    patchEditableLine(
                                      prev,
                                      dt === "Percentage"
                                        ? {
                                            discountPercentage: v,
                                            discountAmount: null,
                                          }
                                        : {
                                            discountAmount: v,
                                            discountPercentage: null,
                                          },
                                      editSnapshotRef.current,
                                    ) ?? prev,
                                );
                              }}
                              sx={{
                                fontSize: "0.875rem",
                                flex: 1,
                                minWidth: 72,
                                "& .MuiOutlinedInput-notchedOutline": {
                                  borderTopRightRadius: 0,
                                  borderBottomRightRadius: 0,
                                },
                                "& .MuiOutlinedInput-root": {
                                  borderTopRightRadius: 0,
                                  borderBottomRightRadius: 0,
                                },
                                "& .MuiFormHelperText-root": {
                                  display: "none",
                                },
                              }}
                            />
                            <Select
                              size="small"
                              value={normalizeDiscountType(edit.discountType)}
                              onChange={(e) => {
                                const newType = e.target
                                  .value as BillingDiscountKind;
                                setEdit((prev) => {
                                  if (!prev) return prev;
                                  const cur = getDiscountInput(prev);
                                  return (
                                    patchEditableLine(
                                      prev,
                                      {
                                        discountType: newType,
                                        ...(newType === "Percentage"
                                          ? {
                                              discountPercentage: cur,
                                              discountAmount: null,
                                            }
                                          : {
                                              discountAmount: cur,
                                              discountPercentage: null,
                                            }),
                                      },
                                      editSnapshotRef.current,
                                    ) ?? prev
                                  );
                                });
                              }}
                              displayEmpty
                              inputProps={{
                                "aria-label": "Discount type",
                              }}
                              sx={{
                                fontSize: "0.875rem",
                                width: 72,
                                "& .MuiOutlinedInput-notchedOutline": {
                                  borderTopLeftRadius: 0,
                                  borderBottomLeftRadius: 0,
                                  borderLeft: "none",
                                },
                                "&.MuiOutlinedInput-root": {
                                  borderTopLeftRadius: 0,
                                  borderBottomLeftRadius: 0,
                                  borderLeft: "none",
                                },
                                "& .MuiSelect-select": {
                                  paddingX: "4px",
                                  borderLeft: "none",
                                },
                              }}
                            >
                              <MenuItem value="Percentage">
                                <PercentIcon fontSize="small" />
                              </MenuItem>
                              <MenuItem value="Amount">AMT.</MenuItem>
                            </Select>
                          </div>
                        </div>
                      ) : hasRowDiscount ? (
                        <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                          <span>{discValueView}</span>
                          {discTypeView === "Percentage" ? (
                            <PercentIcon
                              sx={{
                                fontSize: "1rem",
                                color: "text.secondary",
                              }}
                            />
                          ) : (
                            <span className="text-[10px] font-semibold text-slate-500 leading-none">
                              AMT.
                            </span>
                          )}
                        </span>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell>{line.insDiscount}</TableCell>
                    <TableCell>
                      {isLineExcluded(line) ? "0" : line.insAmount}
                    </TableCell>
                    <TableCell>{getPatientShareDisplay(line)}</TableCell>
                    <TableCell align="center">
                      <span>
                        {Boolean(row?.insCoverage) && !isLineExcluded(row) ? (
                          <VerifiedUserIcon color="success" />
                        ) : (
                          "NA"
                        )}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-2">
                        {Boolean(edit?.id == row?.id && selected == index) ? (
                          <>
                            <Tooltip title="Save" placement="left">
                              {selected == index && saveEditLoading ? (
                                <CircularProgress size={18} color="success" />
                              ) : (
                                <IconButton
                                  onClick={handleEditSave}
                                  size="small"
                                  color="success"
                                >
                                  <DoneIcon />
                                </IconButton>
                              )}
                            </Tooltip>
                            <Tooltip title="Close">
                              <IconButton
                                disabled={deleteLoading || saveEditLoading}
                                onClick={() => {
                                  setEdit(null);
                                }}
                                size="small"
                                color="error"
                              >
                                <CloseIcon />
                              </IconButton>
                            </Tooltip>
                          </>
                        ) : (
                          <>
                            <Tooltip title="Edit" placement="left">
                              <IconButton
                                disabled={
                                  deleteLoading ||
                                  saveEditLoading ||
                                  row.groupId == 3
                                }
                                onClick={() => {
                                  handleEditClick(
                                    {
                                      ...row,
                                      encounterId: parseInt(
                                        encounterId || "-1",
                                      ),
                                    },
                                    index,
                                  );
                                }}
                                size="small"
                                color="primary"
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            {selected == index && deleteLoading ? (
                              <CircularProgress size={18} color="error" />
                            ) : (
                              <Tooltip title="Delete">
                                <IconButton
                                  size="small"
                                  className="text-red-500"
                                  color="error"
                                  disabled={
                                    deleteLoading ||
                                    saveEditLoading ||
                                    row.groupId == 3
                                  }
                                  onClick={() => {
                                    handleDelete(
                                      {
                                        ...row,
                                        encounterId: parseInt(
                                          encounterId || "-1",
                                        ),
                                      },
                                      index,
                                    );
                                  }}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </CustomTableBody>
          </Table>
        </div>
      </TableContainer>
      <Paper>
        <div className="px-3 py-2">
          <Typography fontWeight={600}> Amount details </Typography>
        </div>
        <Divider />
        <Table sx={{ mt: 2 }} size="small" aria-label="simple table">
          <TableBody>
            <TableRow>
              <TableCell sx={{ border: 0 }}>Amount</TableCell>
              <TableCell sx={{ border: 0 }}>
                :{" "}
                <strong>
                  {(displayAmountDetails?.totalAmount ?? 0).toFixed(2)}
                </strong>
              </TableCell>
              <TableCell sx={{ border: 0 }}>Discount Amount</TableCell>
              <TableCell sx={{ border: 0 }}>
                :{" "}
                <strong>
                  {(displayAmountDetails?.totalDiscountedAmount ?? 0).toFixed(2)}
                </strong>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ border: 0 }}>Insurance Amount</TableCell>
              <TableCell sx={{ border: 0 }}>
                :{" "}
                <strong>
                  {(displayAmountDetails?.insAmount ?? 0).toFixed(2)}
                </strong>
              </TableCell>
              <TableCell sx={{ border: 0 }}>Patient Share</TableCell>
              <TableCell sx={{ border: 0 }}>
                :{" "}
                <strong>
                  {(displayAmountDetails?.patShare ?? 0).toFixed(2)}
                </strong>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ border: 0 }} colSpan={4} />
            </TableRow>
            <TableRow>
              <TableCell colSpan={4}>
                <div className="flex gap-3 items-center justify-end">
                  <Button
                    disabled={
                      saveLoading ||
                      !Boolean(allBills?.response?.billingDetails?.length) ||
                      isSaved
                    }
                    endIcon={
                      saveLoading && (
                        <CircularProgress size={18} color="success" />
                      )
                    }
                    color="success"
                    onClick={handleSave}
                  >
                    save
                  </Button>
                  <Button color="error" onClick={goBack}>
                    cancel
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Paper>
    </div>
  );
}

export default BillingDetailsPage;
